import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps';
import { MapPin, Search, LocateFixed, Trash2, Share2, X, Navigation as NavIcon, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;
const DEFAULT_CENTER = { lat: 44.803, lng: -79.719 }; // Port Severn, ON

const STATUS_META = {
  been: { label: 'Been here', color: '#16a34a' },
  going: { label: 'Going here', color: '#f97316' },
};

const MapControls = ({ onPlace }) => {
  const map = useMap();
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);

  const geocodeSearch = async () => {
    if (!search.trim() || !window.google || !map) return;
    setSearching(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: search.trim() }, (results, status) => {
      setSearching(false);
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        const pos = { lat: loc.lat(), lng: loc.lng() };
        map.panTo(pos);
        map.setZoom(16);
        onPlace(pos, results[0].formatted_address);
      } else {
        toast.error('Address not found');
      }
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not supported by this browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        map?.panTo(pos);
        map?.setZoom(17);
        onPlace(pos, null);
      },
      () => toast.error('Could not get your location — check location permissions'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="absolute top-3 left-3 right-3 z-10 flex gap-2">
      <div className="flex flex-1 gap-2 bg-white rounded-lg shadow-lg p-2 max-w-md">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') geocodeSearch(); }}
          placeholder="Search address..."
          className="flex-1 h-10 border-gray-200 text-gray-900"
          data-testid="job-map-search-input"
        />
        <Button onClick={geocodeSearch} disabled={searching} className="h-10 bg-blue-600 hover:bg-blue-700 px-3" data-testid="job-map-search-btn">
          <Search className="w-4 h-4" />
        </Button>
      </div>
      <Button onClick={useMyLocation} className="h-14 w-14 rounded-lg bg-white hover:bg-gray-100 text-blue-600 shadow-lg p-0" data-testid="job-map-my-location-btn">
        <LocateFixed className="w-6 h-6" />
      </Button>
    </div>
  );
};

const MapReadyGate = ({ children }) => {
  const map = useMap();
  if (!map) return null;
  return children;
};

const MapLoadGuard = ({ children }) => {
  const status = useApiLoadingStatus();
  if (status === APILoadingStatus.FAILED || status === APILoadingStatus.AUTH_FAILURE) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-800 p-6" data-testid="job-map-load-error">
        <div className="text-center max-w-md">
          <MapPin className="w-10 h-10 text-orange-400 mx-auto mb-3" />
          <p className="text-white font-semibold mb-1">Google Maps couldn't load on this domain</p>
          <p className="text-slate-400 text-sm">
            The Google Maps API key is restricted to specific websites. Add this site's address
            ({window.location.origin}/*) to the key's allowed referrers in Google Cloud Console.
          </p>
        </div>
      </div>
    );
  }
  return children;
};

const FocusHandler = ({ locations, onSelect }) => {
  const map = useMap();
  const doneRef = React.useRef(false);
  useEffect(() => {
    if (!map || doneRef.current || locations.length === 0) return;
    const focusId = new URLSearchParams(window.location.search).get('focus');
    if (focusId) {
      const loc = locations.find(l => l.id === focusId);
      if (loc) {
        map.panTo({ lat: loc.lat, lng: loc.lng });
        map.setZoom(16);
        onSelect(loc);
      }
    }
    doneRef.current = true;
  }, [map, locations, onSelect]);
  return null;
};

const JobMap = ({ user }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const isAdmin = user.role === 'admin' || user.role === 'supervisor';
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null); // {lat, lng, address}
  const [form, setForm] = useState({ name: '', notes: '', status: 'going', project_id: '', is_global: false });
  const [projects, setProjects] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareIds, setShareIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    window.gm_authFailure = () => setAuthFailed(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/job-locations`);
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load job locations');
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    axios.get(`${API}/projects`).then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/users`).then(r => setTeamUsers((r.data || []).filter(u => u.role !== 'client' && u.id !== user.id))).catch(() => {});
  }, [fetchLocations, user.id]);

  const startDraft = (pos, address) => {
    setSelected(null);
    setDraft({ ...pos, address: address || '' });
    setForm({ name: address ? address.split(',')[0] : '', notes: '', status: 'going', project_id: '', is_global: false });
  };

  const saveLocation = async () => {
    if (!form.name.trim()) {
      toast.error('Please give this job site a name');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/job-locations`, {
        name: form.name,
        lat: draft.lat,
        lng: draft.lng,
        address: draft.address || null,
        notes: form.notes || null,
        status: form.status,
        project_id: form.project_id || null,
        is_global: form.is_global,
      });
      toast.success('Job site added to the map!');
      setDraft(null);
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save job site');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}" from the map?`)) return;
    try {
      await axios.delete(`${API}/job-locations/${loc.id}`);
      toast.success('Job site deleted');
      setSelected(null);
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const shareLocation = async () => {
    if (shareIds.length === 0) {
      toast.error('Select at least one team member');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/job-locations/${shareTarget.id}/share`, { user_ids: shareIds });
      toast.success(`Shared with ${shareIds.length} team member${shareIds.length > 1 ? 's' : ''} — they've been emailed`);
      setShareTarget(null);
      setShareIds([]);
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to share');
    } finally {
      setSaving(false);
    }
  };

  if (!apiKey) {
    return <div className="p-8 text-center text-slate-300">Google Maps API key not configured</div>;
  }

  return (
    <div className="space-y-4" data-testid="job-map-page">
      <div className="glass-card p-4 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <MapPin className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Job Site Map</h1>
            <p className="text-slate-400 text-sm">Tap the map to pin a job site · search an address · or use your location</p>
          </div>
        </div>
        <div className="flex gap-4 mt-3 text-xs">
          <span className="flex items-center gap-1.5 text-slate-300"><span className="w-3 h-3 rounded-full" style={{ background: STATUS_META.been.color }} /> Been here</span>
          <span className="flex items-center gap-1.5 text-slate-300"><span className="w-3 h-3 rounded-full" style={{ background: STATUS_META.going.color }} /> Going here</span>
          <span className="flex items-center gap-1.5 text-slate-300"><span className="w-3 h-3 rounded-full bg-blue-500" /> Company-wide</span>
        </div>
      </div>

      <div className="relative rounded-xl overflow-hidden shadow-xl" style={{ height: '65vh', minHeight: 420 }}>
        {authFailed ? (
          <div className="w-full h-full flex items-center justify-center p-6" style={{ background: '#1e293b' }} data-testid="job-map-load-error">
            <div className="text-center max-w-md">
              <MapPin className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <p className="font-semibold mb-1" style={{ color: '#ffffff' }}>Google Maps couldn't load on this domain</p>
              <p className="text-sm" style={{ color: '#94a3b8' }}>
                The Google Maps API key is restricted to specific websites. Add this site's address
                ({window.location.origin}/*) to the key's allowed referrers in Google Cloud Console.
              </p>
            </div>
          </div>
        ) : (
        <APIProvider apiKey={apiKey} libraries={['marker']}>
          <MapLoadGuard>
          <Map
            style={{ width: '100%', height: '100%' }}
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={11}
            mapId="DAWAL_JOB_MAP"
            gestureHandling="greedy"
            disableDefaultUI={true}
            zoomControl={true}
            onClick={(e) => {
              const ll = e.detail?.latLng;
              if (ll) startDraft({ lat: ll.lat, lng: ll.lng }, null);
            }}
          >
            <MapControls onPlace={startDraft} />
            <FocusHandler locations={locations} onSelect={setSelected} />
            <MapReadyGate>
            {locations.map((loc) => (
              <AdvancedMarker
                key={loc.id}
                position={{ lat: loc.lat, lng: loc.lng }}
                onClick={() => { setDraft(null); setSelected(loc); }}
              >
                <div
                  className="w-9 h-9 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
                  style={{ background: loc.is_global ? '#3b82f6' : (STATUS_META[loc.status]?.color || '#64748b') }}
                  data-testid={`job-marker-${loc.id}`}
                >
                  <MapPin className="w-5 h-5 text-white" />
                </div>
              </AdvancedMarker>
            ))}
            {draft && (
              <AdvancedMarker position={{ lat: draft.lat, lng: draft.lng }}>
                <div className="w-9 h-9 rounded-full border-2 border-white shadow-lg bg-slate-700 flex items-center justify-center animate-bounce">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
              </AdvancedMarker>
            )}
            {selected && (
              <InfoWindow
                position={{ lat: selected.lat, lng: selected.lng }}
                onCloseClick={() => setSelected(null)}
                pixelOffset={[0, -40]}
              >
                <div className="min-w-[220px] max-w-[280px] text-gray-900" data-testid="job-marker-info">
                  <p className="font-bold text-base">{selected.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: STATUS_META[selected.status]?.color }}>
                    {selected.is_global ? 'Company-wide job site' : STATUS_META[selected.status]?.label}
                  </p>
                  {selected.address && <p className="text-xs text-gray-600 mt-1">{selected.address}</p>}
                  {selected.notes && <p className="text-sm text-gray-700 mt-1">{selected.notes}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Added by {selected.created_by === user.id ? 'you' : selected.created_by_name}
                    {selected.shared_with?.includes(user.id) ? ' · shared with you' : ''}
                  </p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold bg-blue-600 text-white px-2.5 py-1.5 rounded-md"
                    >
                      <NavIcon className="w-3.5 h-3.5" /> Directions
                    </a>
                    {(selected.created_by === user.id || isAdmin) && (
                      <>
                        <button
                          onClick={() => { setShareTarget(selected); setShareIds([]); setSelected(null); }}
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-600 text-white px-2.5 py-1.5 rounded-md"
                          data-testid="job-marker-share-btn"
                        >
                          <Share2 className="w-3.5 h-3.5" /> Share
                        </button>
                        <button
                          onClick={() => deleteLocation(selected)}
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-red-600 text-white px-2.5 py-1.5 rounded-md"
                          data-testid="job-marker-delete-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </InfoWindow>
            )}
            </MapReadyGate>
          </Map>
          </MapLoadGuard>
        </APIProvider>
        )}
      </div>

      {/* Locations list */}
      <div className="glass-card p-4 sm:p-6">
        <h2 className="text-lg font-bold text-white mb-3">My Job Sites ({locations.length})</h2>
        {locations.length === 0 ? (
          <p className="text-slate-400 text-sm">No job sites pinned yet — tap the map to add your first one.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {locations.map((loc) => (
              <div key={loc.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg" data-testid={`job-location-row-${loc.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {loc.status === 'been'
                    ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: STATUS_META.been.color }} />
                    : <Clock className="w-5 h-5 flex-shrink-0" style={{ color: STATUS_META.going.color }} />}
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{loc.name}{loc.is_global ? ' · 🏢' : ''}</p>
                    <p className="text-slate-400 text-xs truncate">
                      {loc.is_global ? 'Company-wide' : STATUS_META[loc.status]?.label} · by {loc.created_by === user.id ? 'you' : loc.created_by_name}
                      {loc.shared_with?.length > 0 && loc.created_by === user.id ? ` · shared with ${loc.shared_with.length}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {(loc.created_by === user.id || isAdmin) && (
                    <Button variant="ghost" size="sm" onClick={() => deleteLocation(loc)} className="text-red-400 hover:text-red-300 hover:bg-red-900/20" data-testid={`job-location-delete-${loc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add job site dialog */}
      {draft && (
        <Dialog open={true} onOpenChange={() => setDraft(null)}>
          <DialogContent className="max-w-md" data-testid="add-job-site-dialog">
            <DialogHeader>
              <DialogTitle>Add Job Site</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 block">Job Site Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Smith cottage deck"
                  data-testid="job-site-name-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Status</Label>
                <div className="flex gap-2">
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <button
                      key={key}
                      onClick={() => setForm({ ...form, status: key })}
                      className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${form.status === key ? 'text-white' : 'text-gray-500 border-gray-300'}`}
                      style={form.status === key ? { background: meta.color, borderColor: meta.color } : {}}
                      data-testid={`job-site-status-${key}`}
                    >
                      {meta.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Notes (optional)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Gate code, parking, materials..."
                  className="min-h-[60px]"
                  data-testid="job-site-notes-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Link to Project (optional)</Label>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="job-site-project-select"
                >
                  <option value="">— None —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {isAdmin && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_global}
                    onChange={(e) => setForm({ ...form, is_global: e.target.checked })}
                    className="w-4 h-4 accent-blue-600"
                    data-testid="job-site-global-checkbox"
                  />
                  Show this job site to ALL employees
                </label>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
                <Button onClick={saveLocation} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="job-site-save-btn">
                  {saving ? 'Saving...' : 'Add to Map'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Share dialog */}
      {shareTarget && (
        <Dialog open={true} onOpenChange={() => setShareTarget(null)}>
          <DialogContent className="max-w-md" data-testid="share-job-site-dialog">
            <DialogHeader>
              <DialogTitle>Share "{shareTarget.name}"</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">Selected team members will see this pin on their map and get an email with directions.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {teamUsers.map(u => (
                <label key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:bg-accent" data-testid={`share-user-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={shareIds.includes(u.id)}
                    onChange={() => setShareIds(prev => prev.includes(u.id) ? prev.filter(i => i !== u.id) : [...prev, u.id])}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}{shareTarget.shared_with?.includes(u.id) ? ' (already shared)' : ''}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.role}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShareTarget(null)}>Cancel</Button>
              <Button onClick={shareLocation} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="share-job-site-confirm-btn">
                <Share2 className="w-4 h-4 mr-2" />{saving ? 'Sharing...' : 'Share'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default JobMap;
